import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Spinner } from "@/components/Spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  type AccountInfo,
  type AccountMember,
  ApiError,
  getAccount,
  inviteMember,
  leaveSharedAccount,
  removeMember,
  revokeInvitation,
} from "@/lib/account";
import { authClient } from "@/lib/auth-client";

function AccountPage() {
  const navigate = useNavigate();
  const { refetch: refetchSession } = authClient.useSession();
  const [info, setInfo] = useState<AccountInfo | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    try {
      setInfo(await getAccount());
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loadError) {
    return (
      <p className="text-sm text-muted-foreground">
        Couldn't load your account. Please try again.
      </p>
    );
  }

  if (!info) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  const { isOwner, members, invitations } = info;
  const isShared = members.length > 1;
  const firstOther = members.find((m) => !m.isSelf);

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setInviting(true);
    try {
      await inviteMember(trimmed);
      setEmail("");
      toast.success(`Invitation sent to ${trimmed}.`);
      await load();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Couldn't send invite.",
      );
    } finally {
      setInviting(false);
    }
  }

  async function handleRevoke(id: string) {
    try {
      await revokeInvitation(id);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't revoke.");
    }
  }

  async function handleRemove(member: AccountMember) {
    try {
      await removeMember(member.userId);
      toast.success(`${member.name ?? member.email} was removed.`);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't remove.");
    }
  }

  async function handleLeave() {
    try {
      await leaveSharedAccount();
      // Our accountId changed server-side; refetch the session so the layout's
      // useSync (via syncService.activate) wipes local data and resyncs into our
      // new, empty account, then head back to the list.
      await refetchSession?.();
      navigate({ to: "/shopping" });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't leave.");
    }
  }

  return (
    <div className="flex flex-col gap-8">
      <section>
        <h2 className="mb-1 text-sm font-medium">Members</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Everyone here shares the same Provender account — meal plans, shopping
          list and recipes.
        </p>
        <ul className="flex flex-col divide-y rounded-md border">
          {members.map((member) => (
            <li
              key={member.userId}
              className="flex items-center gap-3 px-3 py-2.5"
            >
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium">
                  {member.name ?? member.email}
                  {member.isSelf && (
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      (You)
                    </span>
                  )}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {member.email}
                </span>
              </div>
              {member.role === "owner" && (
                <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                  Owner
                </span>
              )}
              {isOwner && !member.isSelf && member.role !== "owner" && (
                <RemoveMemberButton
                  member={member}
                  onConfirm={() => handleRemove(member)}
                />
              )}
            </li>
          ))}
        </ul>
      </section>

      {isOwner && (
        <section>
          <h2 className="mb-1 text-sm font-medium">Invite someone</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            They'll get an email invitation. Accepting replaces any existing
            Provender account of their own.
          </p>
          <form onSubmit={handleInvite} className="flex gap-2">
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-label="Email to invite"
            />
            <Button type="submit" disabled={inviting || !email.trim()}>
              Invite
            </Button>
          </form>

          {invitations.length > 0 && (
            <ul className="mt-4 flex flex-col divide-y rounded-md border">
              {invitations.map((inv) => (
                <li
                  key={inv.id}
                  className="flex items-center gap-3 px-3 py-2.5"
                >
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm">{inv.email}</span>
                    <span className="text-xs text-muted-foreground">
                      Pending invitation
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRevoke(inv.id)}
                    className="text-sm text-muted-foreground underline underline-offset-2"
                  >
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {isShared && (
        <section>
          <h2 className="mb-1 text-sm font-medium">Leave account</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Stop sharing and start over with your own empty account.
          </p>
          <LeaveAccountButton
            isOwner={isOwner}
            newOwnerName={firstOther?.name ?? firstOther?.email ?? null}
            onConfirm={handleLeave}
          />
        </section>
      )}
    </div>
  );
}

function RemoveMemberButton({
  member,
  onConfirm,
}: {
  member: AccountMember;
  onConfirm: () => void;
}) {
  const who = member.name ?? member.email;
  return (
    <AlertDialog>
      <AlertDialogTrigger
        render={
          <button
            type="button"
            className="text-sm text-destructive underline underline-offset-2"
          />
        }
      >
        Remove
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {who}?</AlertDialogTitle>
          <AlertDialogDescription>
            {who} will lose access to this shared Provender account and start
            over with a new, empty one. Their meal plans, shopping list and
            recipes here stay with the rest of you.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Remove</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function LeaveAccountButton({
  isOwner,
  newOwnerName,
  onConfirm,
}: {
  isOwner: boolean;
  newOwnerName: string | null;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger render={<Button variant="destructive" />}>
        Leave account
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Leave this shared account?</AlertDialogTitle>
          <AlertDialogDescription>
            {isOwner && newOwnerName
              ? `Ownership will pass to ${newOwnerName}. You will get a new, empty Provender account — the shared meal plans, shopping list and recipes stay with the others.`
              : "You will lose access to this shared Provender account and get a new, empty one. The shared meal plans, shopping list and recipes stay with the others."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Leave</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export const Route = createFileRoute("/_settings/account")({
  component: AccountPage,
  staticData: { title: "Account" },
});
